import type { ListFiltersRequest } from '@/Network/Types/Requests/StationRequests'
import { parseBoolToString } from '@/utils/string'
import { parseCoverageStatuses, serializeCoverageStatuses } from '@/utils/coverageFilters'
import type { FilterDashboardStates } from './useFilterDashboard'

/**
 * Campos do modal "Filtros" → query da grade. Era o corpo de `useStationStore.setFilters`;
 * saiu para cá para o painel do celular gravar exatamente o mesmo formato.
 */
export function mergeMoreFiltersIntoQuery(
  query: ListFiltersRequest,
  filters: FilterDashboardStates
): ListFiltersRequest {
  const {
    doctors,
    valueMin,
    valueMax,
    paymentValueMin,
    paymentValueMax,
    withCheckin,
    withCheckout,
    stationType,
    inCash,
    advanceRequested,
    advancePaid,
    ourTeam,
    status,
    openedStation,
    coverageStatus,
    operationalGroups
  } = filters
  // D-B1: `openedStation=false` no BE já é DOCTOR ∪ UNIDENTIFIED; mandar os dois faria AND.
  const coverageStatusQuery = serializeCoverageStatuses(coverageStatus)
  return {
    ...query,
    doctorIds: doctors?.map((item) => String(item)) ?? undefined,
    valueMin: valueMin?.toString(),
    valueMax: valueMax?.toString(),
    paymentValueMin: paymentValueMin?.toString(),
    paymentValueMax: paymentValueMax?.toString(),
    withCheckin: parseBoolToString(withCheckin),
    withCheckout: parseBoolToString(withCheckout),
    stationType: stationType?.map((item) => String(item)),
    inCash: parseBoolToString(inCash),
    advanceRequested: parseBoolToString(advanceRequested),
    advancePaid: parseBoolToString(advancePaid),
    ourTeam: parseBoolToString(ourTeam),
    openedStation: coverageStatusQuery ? undefined : parseBoolToString(openedStation),
    coverageStatus: coverageStatusQuery,
    status,
    operationalGroups
  }
}

const toNumber = (value?: string): number | undefined => {
  if (value === undefined || value === '') return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

/**
 * `buildListQueryFromRoute` converte os booleanos da URL em `boolean` de verdade (o tipo diz
 * `string`); o `mergeMoreFiltersIntoQuery` grava `'true'`/`'false'`. Aceita os dois formatos.
 */
const toBool = (value?: string | boolean): boolean | undefined =>
  value === undefined ? undefined : value === true || value === 'true'

/**
 * Query → campos do modal. O `filterDashboardStates` só é escrito pelo modal; a URL, as visões
 * e os chips mexem direto na query. Ao abrir o painel do celular, é a query que manda.
 * Não reconstrói `crmStates`/`crms`/unidades/escalas/canais/datas: não vivem nesta parte da query.
 */
export function readMoreFiltersFromQuery(query: ListFiltersRequest): FilterDashboardStates {
  const doctorIds =
    query.doctorIds === undefined
      ? undefined
      : (Array.isArray(query.doctorIds) ? query.doctorIds : [query.doctorIds]).map(Number)
  const coverage = parseCoverageStatuses(query.coverageStatus)
  return {
    doctors: doctorIds,
    valueMin: toNumber(query.valueMin),
    valueMax: toNumber(query.valueMax),
    paymentValueMin: toNumber(query.paymentValueMin),
    paymentValueMax: toNumber(query.paymentValueMax),
    withCheckin: toBool(query.withCheckin),
    withCheckout: toBool(query.withCheckout),
    stationType: query.stationType?.length ? [...query.stationType] : undefined,
    inCash: toBool(query.inCash),
    advanceRequested: toBool(query.advanceRequested),
    advancePaid: toBool(query.advancePaid),
    ourTeam: toBool(query.ourTeam),
    status: query.status,
    openedStation: toBool(query.openedStation),
    coverageStatus: coverage.length ? coverage : undefined,
    operationalGroups: query.operationalGroups?.length ? [...query.operationalGroups] : undefined
  }
}
